/**
 * Every word the interface shows, in one place, so another language is a new
 * dictionary rather than a hunt through the code. English only for now.
 */
const en = {
  appName: 'FoldMyCard',
  loading: 'Loading…',
  somethingWrong: 'Something went wrong. Please try again.',

  // accounts
  signIn: 'Sign in',
  signUp: 'Create account',
  signOut: 'Sign out',
  email: 'Email',
  password: 'Password',
  newPassword: 'New password',
  name: 'Your name',
  passwordHint: 'At least 8 characters.',
  noAccount: 'New here?',
  haveAccount: 'Already have an account?',
  forgotPassword: 'Forgot your password?',
  resetByEmail: 'Choose a new one by email.',
  resetLinkSent: 'Sent. Open the link we emailed to {email} within an hour to choose a new password.',
  signUpIntro: 'Make a card folded from one sheet of paper, with your own pictures.',
  checkEmailTitle: 'Check your email',
  checkEmailBody: 'We sent a link to {email}. Open it to finish creating your account.',
  resend: 'Send the link again',
  resent: 'Sent. It can take a minute to arrive.',
  notVerified: 'This email address is not confirmed yet. We have just sent a new link to it: open it, then sign in. (Not there? Look in spam.)',
  wrongPassword: 'That email and password do not match.',
  troubleSigningIn: 'Locked out or stuck?',
  contactSubjectSignIn: 'I cannot sign in',
  verifiedWelcome: 'Your email is confirmed. Welcome to FoldMyCard.',
  forgotTitle: 'Reset your password',
  forgotBody: 'We will email you a link to choose a new password.',
  sendLink: 'Email me a link',
  forgotSent: 'If there is an account for {email}, a link is on its way. It works for one hour.',
  resetTitle: 'Choose a new password',
  resetDone: 'Password changed. Sign in with the new one.',
  resetBadLink: 'That link has expired or was already used. Ask for a new one.',
  backToSignIn: 'Back to sign in',
  devMailboxHint: 'Running on this computer? Emails are not really sent —',
  devMailboxOpen: 'open the local mailbox',
  devMailboxTitle: 'Local mailbox',
  devMailboxBody: 'On this computer the app does not send email. These are the messages it would have sent, newest first. Click one to use its link.',
  devMailboxOff: 'There is no local mailbox here: this copy of the app sends real email.',
  devMailboxEmpty: 'No messages yet. Create an account and its confirmation will appear here.',
  devConfirm: 'Confirm this email',
  devReset: 'Reset this password',

  // account
  account: 'Account',
  deleteAccount: 'Delete my account',
  deleteAccountBody: 'This deletes your account, and the cards and pictures kept in this browser. Cards on your other devices stay there until you clear that browser\'s data. It cannot be undone. Type your email address to confirm.',
  deleteAccountButton: 'Delete everything',

  // home
  yourCards: 'Your cards',
  myCards: 'My cards',
  newCard: 'New card',
  newCardPlaceholder: 'e.g. Mom’s Birthday',
  create: 'Create',
  noCards: 'No cards yet. Give your first one a name to start.',
  versions: '{n} versions',
  oneVersion: '1 version',
  edited: 'edited {when}',
  leftoverPictures: 'Pictures not in any card',
  leftoverPicturesHint: 'Each card now keeps its own pictures - add them inside the card. These were added here before and are on no card: delete the ones you do not need.',
  upload: 'Add pictures',
  uploading: 'Adding {n}…',
  deletePicture: 'Delete picture',
  pictureInUse: 'This picture is on a card ({titles}). Take it off the card first.',
  deleteCard: 'Delete card',
  confirmDeleteCard: 'Delete “{title}”, all its versions and its pictures?',

  // editor
  allCards: 'All cards',
  saved: 'Saved',
  saving: 'Saving…',
  saveFailed: 'Not saved — retrying',
  conflict: 'This card was changed in another window. Reload to see the latest.',
  reload: 'Reload',
  duplicateVersion: 'Duplicate version',
  deleteVersion: 'Delete version',
  deleteVersionHint: 'Delete the version shown. A card always keeps at least one.',
  confirmDeleteVersion: 'Delete “{label}”? This cannot be undone.',
  zone: 'Frame {n}',
  zoneHere: 'Where it is',
  whereFront: 'Front of the closed card',
  whereBack: 'Back of the closed card',
  whereInside: 'Inside — seen at reveal {step}',
  // the twisted purse's version a052bdb5 (2026-10-19), whose pinwheel view came last; later versions show it as reveal 1
  where_pinwheel: 'On an arm of the pinwheel — seen while folding it',
  where_otherWay: 'Folded the other way round — the front',
  where_otherWayBack: 'Folded the other way round — the back',
  empty: 'Empty',
  choosePicture: 'Choose a picture for frame {n}',
  remove: 'Remove picture',
  zoom: 'Zoom',
  resetPicture: 'Reset position',
  adjustHint: 'Drag the picture on the card to move it; use the mouse wheel over it, or the sliders, to zoom and turn it.',
  editPosition: 'Edit position',
  savePosition: 'Save position',
  choosePictureShort: 'Choose a picture ↓',
  dragHint: 'Drag the picture',
  movePicture: 'Move',
  moveUp: 'Move the picture up',
  moveDown: 'Move the picture down',
  moveLeft: 'Move the picture left',
  moveRight: 'Move the picture right',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  cancel: 'Cancel',
  positionLocked: 'Locked: the picture will not move by accident.',
  movingPictures: 'Moving pictures',
  lockPositions: 'Lock picture positions',
  lockShort: 'Lock',
  lockPositionsHint: 'On: a picture moves only after you press “Unlock to move”, so a stray touch or a scroll cannot move it. Off: drag, zoom and turn the selected picture straight away.',
  lockDefaultTouch: 'Kept for this device. On a phone or tablet it starts on.',
  lockDefaultMouse: 'Kept for this device. On a computer with a mouse it starts off.',
  unlockedHint: 'Every change is saved as you go. To lock pictures until you press “Unlock to move”, turn on Lock above the card.',
  editingZone: 'Moving the picture in frame {n}. Drag it on the card, or use the sliders.',
  blankEditHint: 'Choose "Edit position" to fix it.',
  theme: 'Appearance',
  themeSystem: 'Match my device',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeHint: 'Kept with your account, on every device you sign in on. Printing always uses white paper.',
  termsTitle: 'Welcome to FoldMyCard',
  termsBody: 'Before you start: FoldMyCard is a personal tool for making and folding your own cards. The service and its card templates belong to FoldMyCard; your pictures and text stay yours. By continuing you accept our Terms and Conditions and Disclaimer.',
  termsLink: 'Read the Terms and Conditions',
  disclaimerLink: 'Read the Disclaimer',
  privacyLink: 'Read the Privacy Policy',
  ageConfirm: 'I am 13 years old or older',
  termsAccept: 'I agree',
  termsDecline: 'No, take me back',
  termsUpdatedTitle: 'Our Terms have changed',
  termsUpdatedBody: 'We updated the Terms and Conditions and the Disclaimer on {date}. Please read them and accept the new version to continue.',
  footerCopyright: '© {year} {owner}. All rights reserved.',
  footerTerms: 'Terms',
  footerDisclaimer: 'Disclaimer',
  footerPrivacy: 'Privacy',
  footerContact: 'Contact us',
  contactTitle: 'Contact us',
  contactBody: 'Locked out of your account, a question, an idea, or something not working? Tell us - we read every message and answer by email.',
  contactUs: 'Contact us',
  contactYourEmail: 'Your email (we answer here)',
  contactSubject: 'Subject',
  contactMessage: 'Message',
  contactCount: '{n} of {max} characters',
  contactSend: 'Send',
  contactSent: 'Thank you - your message is on its way. We will answer at {email}.',
  footerHome: 'Home page',
  waitingTitle: 'Thanks for signing up',
  waitingBody: 'Your account is waiting for approval. We will email {email} as soon as it is ready - usually within a day.',
  promoHave: 'Have a promo code?',
  promoWaitingHint: 'A promo code lets you in straight away.',
  promoUse: 'Use the code',
  promoOptional: 'Promo code (if you have one)',
  promoSignUpHint: 'With a code you are in as soon as you confirm your email. Without one, we let new accounts in by hand, usually within a day.',
  declinedTitle: 'Your account was not approved',
  declinedBody: 'FoldMyCard is open to invited people for now. If you think this is a mistake, reply to any of our emails.',
  approvalPending: 'Waiting',
  approvalApproved: 'Approved',
  approvalDeclined: 'Declined',
  approve: 'Approve',
  decline: 'Decline',
  nobodyHere: 'Nobody here.',
  backups: 'Database backups',
  backupsIntro: 'A copy of every account (names, email addresses, sign-in records) is saved each day and kept for 30 days. There are no cards or pictures on the server to copy: they stay on people\'s own devices. Download a copy now and then to keep one of your own.',
  backupNow: 'Make a backup now',
  backupMade: 'Backup made ({kb} KB).',
  noBackupsYet: 'The first backup is made ten minutes after the server starts.',
  download: 'Download',
  approveTitle: 'A new sign-up',
  approveAsk: 'Let this person use FoldMyCard?',
  approvedDone: 'Approved. They have been sent an email to say they can start.',
  declinedDone: 'Declined. They will see that their account was not approved.',
  badApprovalLink: 'This approval link is not valid, or it is over 30 days old. The Accounts page of the admin portal lists everyone.',
  signedUpOn: 'Signed up {when}',
  look: 'Look',
  lookPhoto: 'Photo',
  lookCartoon: 'Cartoon',
  lookBw: 'Black & white',
  lookMakingN: 'Drawing the pictures… {n} of {of}',
  wholeCard: 'Whole card: paper and picture look',
  paper: 'Paper',
  paperPattern: 'Pattern',
  pattern_plain: 'Plain',
  pattern_dots: 'Dots',
  pattern_confetti: 'Confetti',
  pattern_hearts: 'Hearts',
  pattern_stars: 'Stars',
  pattern_snow: 'Snow',
  pattern_words: 'Your words',
  pattern_stripes: 'Stripes',
  pattern_checks: 'Gingham',
  pattern_flowers: 'Flowers',
  pattern_bubbles: 'Bubbles',
  paperColour: 'Paper colour',
  patternColour: 'Pattern colour',
  paperWords: 'Words written over the paper',
  paperHint: 'Fills the paper around and between the shapes, on both sides of the sheet; the shapes keep their pictures and notes. The patterns look right whichever way the paper folds; your words run in one direction for now. Coloured paper uses more ink.',
  monoCard: 'Black & white card',
  monoCardHint: 'The whole card in greys - pictures, paper and writing - to print with black ink only. Turn it off to bring the colours back.',
  allPictures: 'Every picture on this card',
  allPicturesHint: 'Black & white prints with black ink only - handy for a whole class of invitations.',
  pictureLibrary: 'From the picture library',
  pictureLibraryHint: 'Drawings and photographs for the usual occasions. The one you pick becomes a picture of this card.',
  lookMaking: 'Drawing…',
  lookHint: 'Cartoon redraws the picture in flat colours, for anyone who would rather not show a real photo; black & white saves colour ink. Both are made on this device, and you can switch back at any time.',
  prevZone: 'Previous frame',
  nextZone: 'Next frame',
  allZones: 'All frames',
  messageZone: 'This is the message frame: write your words here. A picture behind them works too.',
  notInView: 'Frame {n} is not visible from here.',
  showWhere: 'Show where it is',
  autoFill: 'Fill the card',
  autoFillNote: "Puts this card's pictures in its frames: the best photo of people in the big circle, faces centred and sized to fit, wide pictures in wide frames, and the calmest picture behind the message. Everything is worked out on this device. Press again for another arrangement; change anything afterwards, or undo.",
  autoFilled: 'Pictures placed.',
  undo: 'Undo',
  redo: 'Redo',
  undoHint: 'Undo the last change (Ctrl+Z)',
  redoHint: 'Do it again (Ctrl+Y)',
  editHistory: 'Changes',
  asOpened: 'As opened',
  asOpenedHint: 'Put the card back as it was when you opened it. You can undo this too.',
  asOpenedConfirm: 'Put the card back as it was when you opened it? You can undo this.',
  autoSave: 'Auto-save',
  autoSaveHint: 'On: every change is saved at once. Off: changes wait for Save, and Cancel lets them go.',
  saveChanges: 'Save',
  cancelChanges: 'Cancel changes',
  cancelConfirm: 'Let go of the changes not saved, and go back to the card as last saved? Undo can still bring them back while you are here.',
  notSaved: 'Not saved',
  saveFirstConfirm: 'You have changes that are not saved. Save them now and go on? (Cancel stays here.)',
  leaveUnsaved: 'You have changes that are not saved. Leave without saving them?',
  renameVersion: 'Rename',
  renameVersionHint: 'Give this version a name of your own',
  versionName: 'Name of this version',
  copyHint: 'A copy of this version, to change without touching this one',
  copyHintUnsaved: 'Keep your changes not saved as a new version - this one stays as last saved',
  saveAsCopy: 'Save as new version',
  copyName: 'Name the copy',
  makeCopy: 'Make the copy',
  copyNote: 'The copy starts as this version is now. Changes to one never touch the other.',
  copyNoteUnsaved: 'The copy takes your changes not saved yet; this version stays as it was last saved.',
  oldCardNote: 'You last changed this card {when}. Every change can be undone, and "As opened" puts it all back - or try your changes on a copy.',
  workOnCopy: 'Work on a copy',
  facesLooking: 'Looking for faces… {n} of {of}',
  facesLookingOne: 'Looking for the face…',
  facesNone: 'No face found in this picture: it stays where it is.',
  centreFace: 'Centre the face',
  // the editor's tool palette (palette.tsx)
  palOpenAll: 'Open all',
  palCloseAll: 'Close all',
  palPosition: 'Position & size',
  palPaper: 'Paper & whole card',
  palNone: 'None',
  palFilled: '{n} of {of} filled',
  palLibraryCount: '{n} pictures',
  palCardPictures: "This card's pictures",
  palPictures: 'Pictures',
  palShape: 'Shape',
  shape_circle: 'Circle',
  shape_heart: 'Heart',
  shape_square: 'Square',
  shape_diamond: 'Diamond',
  shape_band: 'Band',
  shapePair_circle: 'Half circles',
  shapePair_heart: 'Half hearts',
  shapePairHint: 'Frames {a} and {b} change together: when the card opens, their two halves meet as one shape.',
  shapeMeet: 'Up or down in Reveal 1',
  slideUp: 'Up',
  slideDown: 'Down',
  slideZone: 'Frame {n}: up or down in Reveal 1',
  slideAt: '{mm} mm {way} from where it was designed',
  lineUp: 'Line them up',
  linedUp: 'Lined up',
  apartBy: '{mm} mm apart',
  lineUpNoRoom: 'Too big to line up here: choose Normal to line them up.',
  shapeSize: 'Size',
  size_normal: 'Normal',
  size_big: 'Bigger',
  shapeNoRoom: 'No room for a bigger one here',
  shapeHint: 'Each shape is made as big as this card allows, and moves only as far as it may: it shows only when its fold opens, and stays clear of the other pictures and of the edge of the sheet. A picture in it is fitted again.',
  // a frame for a small symbol (symbol.tsx, the square letter's points)
  symbol_heart: 'Heart',
  symbol_star: 'Star',
  symbol_moon: 'Half moon',
  symbolNone: 'None',
  symbolHint: 'A small symbol, printed in one colour as big as this point allows. None leaves the paper plain.',
  importOpen: 'From another card',
  importTitle: 'Pictures from your other cards',
  importHint: 'Click a picture to add a copy of it to this card, or add all of a card at once. The other card keeps its own.',
  importNone: 'Your other cards have no pictures yet.',
  importAll: 'Add all {n}',
  importOne: 'Add a copy to this card',
  importHave: 'Already on this card',
  importedN: '{n} added to this card.',
  startWith: 'Start with the pictures of',
  startWithNone: 'no other card - new pictures',
  startWithCard: '{title} ({n} pictures)',
  palPlain: 'Plain paper',
  plainMake: 'Leave plain',
  plainMakeHint: 'Leave this shape out: the paper shows here instead, for a calmer card. Its picture is kept, to bring back at any time.',
  plainUndo: 'Bring the shape back',
  plainHint: 'This shape is left plain: the paper shows here instead.',
  zonePlainShort: 'Frame {n} is plain paper',
  palLookHere: 'Look of this picture',
  zoneEmptyShort: 'Frame {n} is empty',
  pictureTurnLeft: 'Turn left a quarter',
  pictureTurnRight: 'Turn right a quarter',
  popHint: 'Or drag the picture on the card; the mouse wheel zooms.',
  popDone: 'Done',
  palLibrary: 'Picture library',
  palPickBelow: 'Frame {n} is empty: pick one of the pictures below.',
  palOrLibrary: 'Or pick one from the picture library →',
  noPicturesYet: 'Add a picture to put it on the card. Pictures added here belong to this card only.',
  pictureOnThisCard: 'This picture is on the card. Put another one in its place first.',
  pictureOnOtherVersion: 'This picture is on another version of this card. Take it off there first.',
  missingPicture: 'This picture was deleted.',
  viewSheet: 'Print sheet',
  sheetFace: 'Printed side {n}',
  sheetNote: 'How the sheet comes out of the printer. Side 2 is printed on the back.',
  preview: 'Preview',
  close: 'Close',
  previous: 'Previous',
  next: 'Next',
  picture: 'Picture',
  rotate: 'Turn',
  blankWarning: 'Part of this frame will print as blank paper.',
  fillZone: 'Fill the frame',
  numbers: 'Numbers',
  zoomToZone: 'Zoom in on frame {n}',
  zoomToZoneHint: 'Fill the view with the selected frame to see its details. Double-clicking a frame does the same.',
  showWholeCard: 'Show whole card',
  note: 'Note',
  addNote: 'Add a note',
  notePlaceholder: 'Write your message…',
  noteText: 'Note text',
  noteTooLong: 'Too much text for this frame: some of it will not show. Shorten it, or choose "Frame shape".',
  noteShrunk: 'Made smaller to fit the frame.',
  language: 'Language',
  font: 'Font',
  textSize: 'Size',
  style: 'Style',
  bold: 'Bold',
  italic: 'Italic',
  alignment: 'Alignment',
  align_start: 'Align to the start of the line',
  align_center: 'Centre',
  align_end: 'Align to the end of the line',
  colour: 'Colour',
  otherColour: 'Other colour',
  shape: 'Shape',
  textTurn: 'Turn',
  turnLeft: 'Turn the text left',
  turnRight: 'Turn the text right',
  textStraight: 'Straight',
  shapeSquare: 'Square',
  shapeDiamond: 'Diamond',
  shapeZone: 'Frame shape',
  veil: 'Veil behind text',
  removeNote: 'Remove note',
  print: 'Print',
  printTitle: 'Print your card',
  printStepPaper: 'Load {paper} paper.',
  printStepScale: 'In the print dialog choose scale "Actual size" or 100% - never "Fit to page".',
  printStepDuplex: 'Print on both sides: two-sided, flip on the LONG edge.',
  printBoth: 'Print both sides',
  printBothHint: 'In the print dialog, open "More settings" and set "Two-sided" (or "Print on both sides") to flip on the long edge. No such setting? Your printer cannot do it by itself - print one side at a time below.',
  printManualTitle: 'Printing one side at a time',
  printSide1: '1. Print side 1',
  printManualFlip: 'Take the printed sheet, turn it over like the page of a book (keep its top at the top), and put it back in the paper tray.',
  printSide2: '3. Print side 2',
  printManualTest: 'Not sure which way the sheet goes back in? Draw a small arrow pointing up on a blank sheet, put it in the tray the same way you will, and see where the arrow comes out.',
  foldGuides: 'Fold guides',
  foldGuidesHint: 'Every fold line, numbered by the step that folds it, plus dashed lines to line the folds up with. They stay off your pictures and off the outside of the finished card.',
  foldingTitle: 'Folding',
  foldingIntro: 'The numbers match the fold lines on the sheet. Every fold is made towards you.',
  foldingGuideLink: 'Step-by-step folding guide, with your own pictures →',
  howToFold: 'How to fold',
  foldTitle: 'How to fold your card',
  foldIntro: 'Seven folds, about five minutes. Each step shows the fold on your own card: watch it, then do the same with your sheet.',
  foldStepOf: 'Step {n} of {total}',
  foldBefore: 'Before you start',
  foldBeforeShort: 'Start',
  foldDone: 'Your card is finished',
  supportButton: 'Support FoldMyCard',
  // invitations with RSVP (2026-10-21, invite.tsx and pages/rsvp.tsx)
  palInvite: 'Invitation & RSVP',
  palInviteOff: 'Off',
  palInviteStatus: '{n} guests · {yes} yes',
  palInviteStatusOne: '1 guest · {yes} yes',
  invitations: 'Invitations',
  inviteIntro: 'Turn this card into an invitation: add the event and your guest list, and print a card for each guest - with their name, and a code their phone camera opens to answer. You are emailed every answer.',
  inviteNoMessage: 'This design has no message frame to hold an invitation.',
  inviteMake: 'Make this card an invitation',
  inviteEdit: 'Edit details',
  inviteWrite: 'Write the invitation',
  inviteWriteHint: 'Puts the event in the message, starting "Dear [Name]" - each guest’s card says their own name.',
  inviteReplaceConfirm: 'Replace the words in the message with the invitation?',
  inviteGuests: 'Add guests - one name per line',
  inviteGuestsHint: 'Nora, Leo and Mia, The Smith family - one per line',
  inviteAddGuests: 'Add guests',
  inviteRemove: 'Remove {name}',
  inviteRemoveConfirm: 'Remove {name}? Their card’s code will stop working.',
  invitePrintAll: 'Print all invitations ({n})',
  inviteDownloadAll: 'Download all as PDF ({n})',
  invitePrintHint: 'One card per guest, each with their name for [Name] and their own RSVP code. The card on screen shows the first guest.',
  inviteMaking: 'Making invitations… {n} of {of}',
  inviteDelete: 'Delete this event',
  inviteRemoveYes: 'Remove',
  inviteArchived: 'This event is in your archive: its codes don’t work until you restore it.',
  inviteOtherCard: 'Another card',
  inviteJoin: 'Or add this card to an event you have',
  inviteJoinGo: 'Add to this event',
  inviteJoinHint: 'One event can have several cards - one for family, one for friends - each printed for its own guests.',
  inviteSeveral: 'This event has {cards} cards: {guests} guests in all, {yes} yes.',
  inviteThisCard: 'This card:',
  inviteGuestsThisCard: 'Add guests to this card - one name per line',
  inviteMove: 'Move {name} to another card',
  inviteMoveTo: 'Move to…',
  eventDeleteTitle: 'Delete “{title}”?',
  eventDeleteBody: 'It goes to your archive for 30 days, with its guests and answers, and you can bring it back from there. Meanwhile its codes stop working. The cards themselves stay.',
  eventDeleteYes: 'Delete',
  eventRestore: 'Restore',
  inviteDear: 'Dear',
  inviteYouAre: 'you are invited to',
  inviteRsvp: 'RSVP',
  inviteScan: 'Scan to RSVP',
  invitePrintingFor: 'Invitations for {n} guests: one card each, in the order of your list.',
  inviteNoGuests: 'Add your guests in the editor (Invitation & RSVP) to print a card for each.',
  eventTitle: 'Event name',
  eventTitleHint: 'Sam’s 10th birthday',
  eventKind: 'Kind of event',
  eventDate: 'Date',
  eventTime: 'Time',
  eventPlace: 'Address',
  eventPlaceHint: '12 Oak Street, Springfield',
  eventContact: 'RSVP contact (shown to guests)',
  eventContactHint: 'Text Ali, 555-0100',
  eventNote: 'Note for guests (optional)',
  eventNoteHint: 'Swimsuits welcome!',
  eventPrivacy: 'The event and your guests’ names stay on this device and in your card files - not on FoldMyCard. Our server keeps only each guest’s code and answer, so their phone can reply.',
  inviteGuestN: 'Guest {n}',
  inviteElsewhere: 'This event’s details and guests’ names are on the device it was made on. Open one of its card files here to bring them.',
  eventSave: 'Save',
  eventKind_birthday: 'Birthday',
  eventKind_wedding: 'Wedding',
  'eventKind_baby-shower': 'Baby shower',
  eventKind_graduation: 'Graduation',
  eventKind_party: 'Party',
  eventKind_dinner: 'Dinner',
  eventKind_holiday: 'Holiday',
  eventKind_other: 'Event',
  rsvpYes: 'Yes',
  rsvpNo: 'No',
  rsvpMaybe: 'Maybe',
  rsvpWaiting: 'Waiting',
  rsvpComing: '{n} people coming',
  rsvpTitle: 'Invitation',
  rsvpHello: 'Hello {name}, you are invited!',
  rsvpHelloAnon: 'Hello, you are invited!',
  rsvpOnYourCard: 'When, where and who to ask are on your card.',
  rsvpFrom: 'from {host}',
  rsvpAsk: 'Will you come?',
  rsvpYourAnswer: 'Your answer - change it any time',
  rsvpYesLong: 'Yes, I’ll be there',
  rsvpMaybeLong: 'Maybe',
  rsvpNoLong: 'Sorry, I can’t',
  rsvpHowMany: 'How many of you are coming, you included?',
  rsvpMessage: 'A note for the host (optional)',
  rsvpMessageHint: 'Can’t wait! / We’ll bring dessert',
  rsvpSend: 'Send my answer',
  rsvpThanksYes: 'Thank you - the host has your answer!',
  rsvpThanksNo: 'Thank you for letting the host know.',
  rsvpChangeLater: 'Plans change? Scan the code again, or open this page, to change your answer.',
  rsvpChange: 'Change my answer',
  rsvpGone: 'This invitation code is not valid any more: the event may have been deleted. Please ask the host.',
  rsvpSample: 'This is a sample code: once guests are added, each guest’s card carries a code of its own that opens their invitation here.',
  rsvpAbout: 'Made with FoldMyCard. Your answer and note go to the host only.',
  rsvpJoinTitle: 'Make your own cards',
  rsvpJoinText: 'FoldMyCard is free: print a card with your own pictures and fold it in minutes - or invite your own guests, like this.',
  rsvpJoinButton: 'Create a free account',
  rsvpJoinSignIn: 'I have an account',
  // a code for the website itself (2026-10-23, site-code.tsx)
  siteCodeTitle: 'On your phone?',
  siteCodeText: 'Point your phone’s camera at the square: it opens FoldMyCard’s sign-in there.',
  siteCodeLabel: 'Code that opens FoldMyCard’s sign-in page',
  // the events dashboard on the main page (2026-10-23, events-board.tsx)
  eventsTitle: 'My events',
  eventsNew: 'New event',
  eventsEmpty: 'No events yet. Planning a birthday, a wedding or a party? Press “New event”: a card for every guest, each with their own name and a code to RSVP - and every answer shows up right here.',
  eventsElsewhereTitle: 'An event from another device',
  eventsElsewhereHint: 'Its name, address and guests are on the device it was made on: open one of its card files here.',
  eventsUpcoming: 'upcoming',
  eventsAnswered: 'guests answered',
  eventsAnsweredOf: '{n} of {of}',
  eventsComing: 'people coming',
  eventsNoneUpcoming: 'Nothing coming up.',
  eventsPast: 'Past events ({n})',
  eventsToday: 'Today',
  eventsTomorrow: 'Tomorrow',
  eventsInDays: 'In {n} days',
  eventsYesterday: 'Yesterday',
  eventsDaysAgo: '{n} days ago',
  eventsNoGuests: 'No guests yet',
  eventsOpen: 'Guests & answers',
  eventsCardElsewhere: 'Its card is on another device',
  eventsCardGuests: '{n} guests',
  eventsCardGuestsOne: '1 guest',
  eventsAddCard: 'Add a card',
  eventsAddCardHint: 'Another card for this event - for a different group of guests',
  eventsDelete: 'Delete event',
  eventsArchive: 'Archive ({n})',
  eventsArchiveHint: 'Deleted events stay here for 30 days, then go for good. Restore one to make its codes work again.',
  eventsArchivedOn: 'Deleted {when} · goes for good on {on}',
  eventsForever: 'Delete forever',
  eventsForeverTitle: 'Delete “{title}” forever?',
  eventsForeverBody: 'Its guests and every answer go too. This cannot be undone.',
  eventsRestored: '“{title}” is back, and its codes work again.',
  eventsDeleted: '“{title}” is in the archive.',
  // making something new: a card, or an event (2026-10-23, pages/new-card.tsx)
  chooseTitle: 'What would you like to make?',
  chooseCard: 'A single card',
  chooseCardAbout: 'One folded card for one person: a birthday, a thank-you, a hello.',
  chooseEvent: 'An event',
  chooseEventAbout: 'Invitations for a group: one card per guest, each with their name and a code to RSVP. Answers show on your events.',
  wizTitleEvent: 'Make an event',
  wizStepEvent: 'The event',
  wizStepGuests: 'Your guests',
  wizGuestsHint: 'One name per line - each guest gets a card with their name. You can add more later.',
  wizCreateEvent: 'Create my event',
  wizChangeChoice: '← Card or event',
  wizTitleEventCard: 'A new card for {title}',
  wizEventCardName: 'Name this card',
  wizEventCardHint: 'Only you see this name: who this card is for - “Family” or “Friends”, say.',
  wizCreateEventCard: 'Create this card',
  wizCannotUse: 'Can’t use',
  addProblemsTitle: 'Some pictures could not be added to this card:',
  tipVenmo: 'Tip on Venmo',
  pausedTitle: 'FoldMyCard is taking a short break',
  pausedText: 'The site is paused for a little while. Please come back later.',
  pausedCards: 'Your cards and pictures are safe: they are kept on your own device.',
  signUpsClosed: 'FoldMyCard is not taking new accounts just now. Please try again later.',
  controlsTitle: 'Site controls',
  controlsIntro: 'Changes take effect within a few seconds and are kept in the activity log.',
  ctlApproval: 'Approve new accounts before they can use the app',
  ctlApprovalHint: 'Off: an account is let in as soon as its email is confirmed. Turning it off also lets in everyone waiting now.',
  ctlSignUps: 'New sign-ups are open',
  ctlSignUpsHint: 'Off: nobody new can make an account. Everyone else carries on as usual.',
  ctlNotifySignUps: 'Email me about each new account',
  ctlNotifySignIns: 'Email me about each sign-in',
  ctlNotifyHint: 'At most 40 emails an hour: past that, one warning, then quiet until the hour is up. Your own sign-ins are not reported.',
  ctlPausedNow: 'The website is paused. Only this admin portal works.',
  ctlPause: 'Pause the website',
  ctlPauseHint: 'For an emergency, such as an attack: everything but this admin portal answers "paused" at once, and nobody can sign in or sign up.',
  ctlPauseConfirm: 'Pause the whole website now? Everyone but administrators is stopped until you turn it back on.',
  ctlResume: 'Turn the website back on',
  ctlPausedMessage: 'Message people see while it is paused (optional)',
  ctlLetIn: 'Approval is off: {n} waiting accounts were let in and told.',
  ctlSaved: 'Saved.',
  setPaused: 'paused the website',
  setResumed: 'turned the website back on',
  setApprovalOn: 'switched approval on',
  setApprovalOff: 'switched approval off (let in {n} waiting)',
  setSignUpsOpen: 'opened sign-ups',
  setSignUpsClosed: 'closed sign-ups',
  setMessage: 'changed the paused message',
  setChanged: 'changed the site controls',
  on: 'on',
  off: 'off',
  foldDoneShort: 'Done',
  foldTip: 'Tip',
  foldCheck: 'Check',
  foldPlay: 'Play',
  foldPause: 'Pause',
  foldReplay: 'Play again',
  foldProgress: 'How far the fold has gone',
  foldSide1: 'Side 1',
  foldSide2: 'Side 2',
  foldSideLegend: 'In the pictures, side 2 of the paper is tinted so you can tell the sides apart. Your sheet is white on both sides.',
  foldAll: 'All steps at a glance',
  foldPrint: 'Print these instructions',
  foldHere: 'Fold',
  foldResult: 'Result',
  foldNeed: 'You need your printed sheet and a flat, hard table. A ruler, or the back of a spoon, helps make sharp creases.',
  foldCheckPrint: 'Check the print: US Letter, both sides (two-sided, flip on the long edge), at actual size. The pictures show your two sides.',
  foldTowards: 'Every fold is made towards you: lift the paper, bring it over towards you and lay it down, then press the crease flat.',
  foldLines: 'The faint solid lines on the sheet are the folds, and the number beside each line is the step that folds it. The two dashed lines, one down the middle and one across the sheet, are there to line folds up with: you never fold on them.',
  foldStart: 'Lay the sheet on the table with side 1 facing up, the way it looks in the picture of side 1.',
  foldDoneText: 'The card stays shut on its own, with the pictures on its front the right way up.',
  foldOpens: 'How it opens',
  foldOpensText: 'This is what the person you give it to will see, one step at a time.',
  foldSeeOpen: 'Watch it open',
  foldClosedFront: 'Front',
  foldClosedBack: 'Back',
  foldNoGuide: 'The folding guide for this card is not available.',
  downloadPdf: 'Download PDF',
  sharePdf: 'Share',
  shareHint: 'Send the PDF with your phone\'s or computer\'s own share menu: Mail, Messages, AirDrop and more.',
  sending: 'Making your PDF…',
  pdfReady: 'Your PDF is ready.',
  shareUnsupported: 'This browser cannot share files, so the PDF was downloaded instead. Attach it to an email from there.',
  saveCard: 'Save card to file',
  saveCardHint: 'One file with this card, all its versions and its pictures: to open it on another device, or to keep a copy.',
  savingCard: 'Saving the card…',
  cardSaved: 'Saved. Open this file from the FoldMyCard home page on any device to carry on there.',
  onDevice: 'Your cards and pictures are kept only on this device, in this browser. Nothing is uploaded. Keep a copy of them below, in “Keeping your cards safe”.',
  keepSafe: 'Keeping your cards safe',
  savedInFolder: 'Saved · in your folder',
  chooseFolder: 'Keep my cards in a folder',
  chooseFolderHint: 'Pick a folder on this computer, for example Documents › FoldMyCard. Every card is then saved there as its own project file while you work. If this browser ever loses its data, choose the folder again and your cards come back. A folder in OneDrive, Dropbox or iCloud Drive also brings your cards to your other computers.',
  savingToFolder: 'Your cards are saved in the folder “{name}”',
  lastSaved: 'last saved {when}',
  changeFolder: 'Change folder',
  stopFolder: 'Stop saving to the folder',
  folderOnHint: 'One project file per card, updated a moment after each change. Opening FoldMyCard here also picks up cards changed in that folder from another computer.',
  folderNeedsOk: 'Your card folder “{name}” needs your OK again.',
  reconnectFolder: 'Allow the folder',
  folderNeedsOkHint: 'Browsers ask again after a while. Choose “Allow on every visit” to stop being asked.',
  folderError: 'Your cards could not be saved to the folder “{name}”.',
  tryAgain: 'Try again',
  backupAll: 'Save all my cards',
  backupAllHint: 'One file with every card and its pictures. On a phone, save it to Files or iCloud Drive. To bring the cards back - here or on another device - use “Open a card file” beside it.',
  lastBackup: 'Last copy: {when}',
  noBackupYet: 'No copy made yet.',
  oneCardNotBackedUp: '1 card has changed since your last copy.',
  cardsNotBackedUp: '{n} cards have changed since your last copy.',
  backupReady: 'Your backup is ready.',
  backupMadeCards: 'Backup made. Keep the file somewhere safe.',
  backupOpened: 'Backup opened: {added} cards added, {updated} updated, {kept} already up to date.',
  homeScreenTip: 'On iPhone and iPad, add FoldMyCard to your Home Screen (Share › Add to Home Screen) so Safari keeps your cards even when you have not used it for a while.',
  gotIt: 'Got it',
  openCardFile: 'Open a card file',
  openCardFileHint: 'A card or a backup saved from FoldMyCard, on this device or another. A newer copy of a card you have updates it.',
  cardFileFailed: 'That card file could not be opened.',
  view_f1: 'Front',
  view_f2: 'Back',
  view_f3: 'Reveal 1',
  view_f4: 'Reveal 2',
  view_f5: 'Reveal 3',
  view_f6: 'Reveal 4',
  view_f7: 'Reveal 5',
  view_pinwheel: 'Pinwheel',
  // the paper banger folded the other way round (2026-10-25): its other half, mirrored
  view_otherWay: 'Other way, front',
  view_otherWayBack: 'Other way, back',
  chooseDesign: 'Design',
  designClassic: 'Classic pocket card',
  designClassicAbout: 'Portrait, 13 frames, opens in three steps to a big round photo.',
  designAligned: 'Classic, circles lined up',
  designAlignedAbout: 'The classic card, with the two half circles of the first reveal meeting as one circle. A trial: print one and fold it.',
  designLetterfold: 'Letterfold envelope',
  designLetterfoldAbout: 'A square envelope with a seal, 13 frames, opens in four steps to a whole letter.',
  designSurprise: 'Surprise card',
  designSurpriseAbout: 'Fold it and four corners meet as one hidden photo. Open it for four more photos and the message. Four folds. A trial: print one and fold it.',
  designTwisted: 'Twisted puzzle purse',
  designTwistedAbout: 'The traditional pinwheel purse, woven shut by one twist: four small photos on the front, one on the back, eight more when its arms are lifted, and four around the message inside. One cut, then one twist. A trial: print one and fold it.',
  designTeller: 'Fortune teller',
  designTellerAbout: 'The playground fortune teller: four photos to pick, eight numbers, eight fortunes, and a hidden picture inside. One cut, seven folds. A trial: print one and fold it.',
  designBanger: 'Paper banger',
  designBangerAbout: 'The traditional party popper: snap it and a pocket bursts out with a bang and your message. A photo on each side, BANG! on both sides of the pocket, the details inside - and it folds either way round. No glue, no scissors. A trial: print one and fold it.',
  designPacket: 'Hexagonal packet',
  designPacketAbout: 'The traditional seed packet: a long six-sided card whose ends weave shut under the hem, with room inside for a gift card. A big photo on the front, three on the back, two more when the points are opened, the message inside. No glue; two tiny snips. A trial: print one and fold it.',
  designSquare: 'Square letter',
  designSquareAbout: 'The traditional square letterfold, first seen on a letter of 1694: a letter that folds into its own envelope, its two corners locked under a band. Two photos on the front beside a line of words, two on the back, two big ones as it opens, the message inside. No glue, no scissors. A trial: print one and fold it.',
  designFrame: 'Photo frame',
  designFrameAbout: 'The traditional decorative frame, published in Paris in 1894: the sheet folds into a frame round its own photo, the four corners meeting in neat mitres. A big photo in the window, a line of words across the top and bottom, a heart either side, the message on the back. No glue, no scissors. A trial: print one and fold it.',

  // each design's story, behind the (i) beside it in "Choose a design" (design-story.tsx): on screen only.
  // Dates are the earliest records known to the Public Paperfolding History Project - "recorded",
  // never "invented" (docs/fold-ideas/traditional-models.md)
  storyOpen: 'The story of the {name}',
  storyTitle: 'The {name}: its story',
  storySource: 'Dates are the earliest records known to David Mitchell\'s Public Paperfolding History Project (origamiheaven.com). They are records, not inventions: each fold may be older still. Every picture and diagram in FoldMyCard is our own.',
  story_classic: 'FoldMyCard\'s first design: one sheet that folds into a pocket card and opens in three steps, ending with the big round photo.\n\nWho first folded a card this way is not recorded. What FoldMyCard adds is its own: the thirteen photo frames, each placed to be found as the card opens, the folding lines printed on the sheet and the step-by-step guide.',
  story_purse: 'Families in Japan folded this purse to keep thread and small treasures: it appears in a pattern book of 1719, Hinagata kiku no nae. A love letter folded the same way was sent in Philadelphia in 1769, and the first printed instructions came in 1819, in a children\'s book by Heinrich Rockstroh in Berlin.\n\nFour folds and one tuck weave it shut with no loose end, which is why it holds without glue. In Japan it is also known as menko, the name of a game played by flipping such purses.',
  story_teller: 'Children folded this toy long before it told fortunes. It is mentioned in Philadelphia in 1836, called the salt cellar - la salière - in a kindergarten manual of 1859, and drawn step by step in 1876. A game played with it is recorded in Madrid in 1907, and fortunes written under its flaps in 1959.\n\nPick a colour, count the moves, open a flap: FoldMyCard puts your photos on the colours and your own words under the flaps.',
  story_twisted: 'The puzzle purse as it was folded in Japan: the same pattern book of 1719 shows a purse whose corners twist round into a pinwheel, each arm holding the next. In China it was drawn in 1914 as the camphor bag, and the treasure-chest cube called tamatebako is probably six of them joined together.\n\nOne twist closes it, and it stays woven shut by itself.',
  story_banger: 'The party popper of the schoolyard. A London book of boys\' pastimes described it in 1840, T. Kentish\'s The School Boy\'s Holiday Companion; a double one appears in Amsterdam in 1863, and German children folded it as the Klatsche.\n\nHold it by the tip and snap it down: the pocket inside swings out with a bang. FoldMyCard prints your news on that pocket - and you can push it back in and bang it again.',
  story_packet: 'A gardener\'s seed packet. Packets folded this way, dated 1803 and labelled in the hands of William Hamilton and the botanist William Bartram, were found in the attic of The Woodlands, Hamilton\'s house in Philadelphia. It was first drawn in a German book of games in 1864, as a paper bag.\n\nBoth pointed ends tuck under the hem, so it carries seeds, a gift card or a ticket without glue.',
  story_square: 'Before there were envelopes, people folded each letter into its own cover. A letter folded exactly this way, dated 1694, is kept in the Brienne collection in The Hague: written, its readers say, by a Dutch soldier held prisoner in France to his wife at home, and never delivered. The fold was printed as instructions in Berlin in 1819, taught in Japanese and Chinese schools in the 1900s, and in 1961 an origami magazine called it a letter that seals itself, to post without an envelope.\n\nIts two bottom corners tuck under a band at the top: the letter locks itself.',
  story_frame: 'French primary schools of the 1890s taught paper folding. This frame, the Cadre, was published for their teachers in Paris on 17 February 1894: crease a band along each side, fold each corner in through the point where its two creases cross, then fold the sides in. It appears again in Paris in 1897, in Buenos Aires in 1899, in a Tokyo teaching handbook in 1905, and as a square frame in 1951.\n\nFolding the corners first is the trick: the bands meet at each corner in a mitre, like a joiner\'s frame. FoldMyCard prints your photo in the window and your words on the bands.',
  designPurse: 'Puzzle purse',
  designPurseAbout: 'A traditional woven purse that holds itself shut, 14 frames, opens in five steps to the message. A trial: print one and fold it.',

  // the front page (pages/landing.tsx)
  landingArtLabel: 'A birthday card for Eva, nine, with the photo of her and her dog in the big circle inside: shut, and opened',
  landingTitle: 'Your family photos, folded into a card they will keep',
  landingLead: 'FoldMyCard turns your own pictures into a greeting card folded from a single sheet of paper. It opens step by step, and every fold reveals another photo. It is this simple:',
  landingStart: 'Make your first card',
  landingInvite: 'FoldMyCard is open by invitation for now: create an account and we will let you in, usually within a day.',
  landingOpen: 'Free to use: create an account, confirm your email, and start your first card.',
  landingHow: 'How it works',
  landingWatch: 'See a card made, start to finish',
  landingWatchAbout: 'Photos placed, the sheet printed on both sides, the seven folds, and the card opening - in a minute and a half, with music.',
  landingStep1: 'Add your pictures',
  landingStep1Body: 'Pick photos from your phone or computer and place them on the card. Move, zoom and turn each one until it looks right. They never leave your device.',
  landingStep2: 'Write your message',
  landingStep2Body: 'Add a few words - in any language, left to right or right to left - and preview the card opening, fold by fold.',
  landingStep3: 'Print and fold',
  landingStep3Body: 'Print one sheet on both sides at home, then follow the animated guide made from your own card. About five minutes.',
  landingPrivateTitle: 'Private',
  landingPrivate: 'Your pictures and cards stay on your own device: they are never uploaded. No ads, no trackers, and nothing is ever sold.',
  landingCartoonTitle: 'Photo, cartoon or black & white',
  landingCartoon: 'Rather not show real faces? Turn any picture into a cartoon with one click. Printing a whole class of invitations? One switch makes the card black and white, for black ink only.',
  landingPaperTitle: 'Plain paper',
  landingPaper: 'Any home printer and one sheet of US Letter paper. No glue, and most designs need no scissors at all.',
  landingDesignsTitle: 'Eight designs',
  landingDesigns: 'Our classic pocket card, and traditional origami: a puzzle purse, a twisted purse, a fortune teller, a paper banger that pops, a hexagonal packet that holds a gift, a square letter that seals itself and a photo frame. Five need no scissors; three need just a cut or two.',
  landingEnd: 'Ready to make someone smile?',

  // signing out after a while without activity (idle.tsx)
  idleTitle: 'Are you still there?',
  idleBody: 'For your security, you will be signed out in {time} because nothing has happened for a while.',
  idleStay: 'Stay signed in',
  idleSignedOut: 'You were signed out because nothing happened for a while. Sign in again to carry on where you were.',
  idleHint: 'For your security, FoldMyCard signs you out after {minutes} minutes without activity.',

  // account settings
  profile: 'Profile',
  saveName: 'Save name',
  nameSaved: 'Name saved.',
  emailAddress: 'Email address',
  emailFixed: 'This is the address you sign in with. To keep your account safe, it cannot be changed.',
  changePassword: 'Change password',
  currentPassword: 'Current password',
  confirmPassword: 'New password again',
  passwordsDiffer: 'The two new passwords are not the same.',
  signOutOthers: 'Sign out of my other devices',
  passwordChanged: 'Password changed. We emailed you a note about it, so an unexpected change is noticed.',
  wrongCurrentPassword: 'That is not your current password.',
  devices: 'Signed-in devices',
  thisDevice: 'This device',
  signedInSince: 'signed in {when}',
  lastUsed: 'last used {when}',
  signOutDevice: 'Sign out',
  signOutOtherDevices: 'Sign out of all other devices',
  noOtherDevices: 'You are not signed in anywhere else.',
  suspendedTitle: 'Your account is suspended',
  suspendedBody: 'Your access to FoldMyCard has been suspended. Your cards and pictures stay on your device. If you think this is a mistake, contact us.',

  // the admin portal (pages/admin.tsx)
  admin: 'Admin',
  adminPortal: 'FoldMyCard Admin',
  adminSignInTitle: 'Administrator sign-in',
  adminSignInIntro: 'This is the FoldMyCard admin portal. Only administrator accounts can sign in here.',
  notAdminTitle: 'Not an administrator',
  notAdminBody: '{email} is not an administrator account. To make cards, use the app.',
  openApp: 'Open the app',
  overview: 'Overview',
  accounts: 'Accounts',
  activity: 'Activity',
  navBackups: 'Backups',
  navPromo: 'Promo codes',
  factPromo: 'Promo code',
  promoNotUsed: 'not used yet',
  promoIntro: 'A promo code lets new accounts in straight away, without waiting for you to approve them. People type it when they sign up or on the waiting page - or you send them a sign-up link that carries it. Give a code a limit and a last day if you like, and switch it off whenever you want.',
  promoNew: 'Make a code',
  promoCodeLabel: 'Code',
  promoCodeAuto: 'Leave empty to make one up',
  promoNote: 'Note',
  promoNoteHint: 'Who it is for, e.g. "Family"',
  promoMaxUses: 'How many people',
  promoNoLimit: 'No limit',
  promoLastDay: 'Last day',
  promoMake: 'Make the code',
  promoMade: 'Code {code} made.',
  promoNone: 'No codes yet.',
  promoUsed: 'Used',
  promoNoEnd: 'No end',
  promoOff: 'Switched off',
  promoExpired: 'Past its last day',
  promoUsedUp: 'Used up',
  promoWorking: 'Working',
  promoCopyCode: 'Copy code',
  promoCopyLink: 'Copy sign-up link',
  promoCopied: 'Copied.',
  promoSwitchOff: 'Switch off',
  promoSwitchOn: 'Switch on',
  promoDelete: 'Delete',
  promoDeleteConfirm: 'Delete the code {code}? Nobody has used it yet.',
  statTotal: 'Accounts',
  statWaiting: 'Waiting for approval',
  statActive: 'Active',
  statSuspended: 'Suspended',
  statDeclined: 'Declined',
  statUnconfirmed: 'Email not confirmed',
  statSignUps: 'New sign-ups, 30 days',
  statSignUpsSub: '{week} in the last 7 days',
  statActiveWeek: 'Active this week',
  subscriptionsTitle: 'Subscriptions',
  subscriptionsOff: 'Not switched on yet. While FoldMyCard is invitation-only, every approved account uses it free. When subscriptions arrive, each account\'s plan and payments will show here.',
  planFree: 'Free (invitation)',
  waitingForYou: 'Waiting for your decision',
  nobodyWaiting: 'Nobody is waiting.',
  recentActivity: 'Recent activity',
  allActivity: 'All activity →',
  noActivity: 'Nothing yet.',
  searchAccounts: 'Search by name or email',
  filterAll: 'All',
  filterAdmins: 'Administrators',
  colAccount: 'Account',
  colStatus: 'Status',
  colJoined: 'Joined',
  colLastActive: 'Last active',
  never: 'never',
  adminBadge: 'Administrator',
  unconfirmedBadge: 'Email not confirmed',
  statusSuspended: 'Suspended',
  backToAccounts: '← Accounts',
  factJoined: 'Joined',
  factLastSignIn: 'Last sign-in',
  factLastActive: 'Last active',
  factDecided: 'Status set',
  factTerms: 'Terms accepted',
  factTermsNot: 'not yet',
  factTermsOld: '{version} (an earlier version)',
  factPlan: 'Plan',
  factTheme: 'Appearance',
  actions: 'Actions',
  adminManagedHint: 'Administrator accounts are set by the ADMIN_EMAILS setting on the server, so they cannot be suspended or deleted here.',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  confirmSuspend: 'Suspend {email}? They are signed out at once and cannot use FoldMyCard until you reactivate them. Their cards and pictures stay on their own devices.',
  signOutEverywhere: 'Sign out everywhere',
  signedOutN: 'Signed out of {n} device(s).',
  sendPasswordReset: 'Send a password reset email',
  passwordResetSent: 'Password reset email sent to {email}.',
  resendConfirmation: 'Send the confirmation email again',
  confirmationSent: 'Confirmation email sent to {email}.',
  noDevices: 'Not signed in anywhere.',
  deleteThisAccount: 'Delete this account',
  deleteThisAccountBody: 'This deletes the account for good; suspending keeps it instead. (Their cards and pictures are on their own devices, not here.) Type the account\'s email address to confirm.',
  accountDeleted: 'The account of {email} was deleted.',
  history: 'History',
  logBy: 'by {by}',
  act_approve: 'approved',
  act_decline: 'declined',
  act_suspend: 'suspended',
  act_reactivate: 'reactivated',
  'act_set-waiting': 'moved back to waiting',
  'act_sign-out': 'signed out everywhere',
  'act_password-reset': 'sent a password reset to',
  'act_resend-confirmation': 'resent the confirmation email to',
  act_delete: 'deleted the account of',
  act_backup: 'made a database backup',
  'act_backup-download': 'downloaded a database backup',
  'act_library-add': 'added a picture to the library',
  'act_library-delete': 'removed a picture from the library',
  navLibrary: 'Picture library',
  libraryTitle: 'Picture library',
  libraryIntro: 'Everything here, the occasion drawings and the photographs, is the picture library everyone sees, in the groups you choose: the occasions first, then the others in alphabetical order. Pictures you add are made smaller in your browser first (1600 px, plus a thumbnail), so each takes a few hundred KB. Only add pictures you own or whose licence allows commercial use.',
  libraryAdd: 'Add a picture',
  libraryFile: 'Picture',
  libraryName: 'Name',
  libraryGroup: 'Group',
  libraryGroupHint: 'An existing group, or a new one.',
  libraryUpload: 'Add to the library',
  libraryAdding: 'Adding…',
  libraryAdded: '“{name}” is in the library.',
  libraryAddedN: '{n} added to the library.',
  libraryAddingN: 'Adding {n} of {of}…',
  libraryFilesHint: 'Choose one picture, or several at once - for example a whole folder of library candidates.',
  libraryManyChosen: '{n} pictures chosen. Each is named after its file; all go into the group below.',
  libraryEmpty: 'The library is empty: nobody sees any pictures in it until you add some.',
  libraryRemove: 'Remove',
  libraryConfirmRemove: 'Remove “{name}” from the library? Cards that already use it keep their copy.',

  // the front page, 2026-10-17: how it works in one line, and the video up top
  landingMini1: 'Add your photos',
  landingMini2: 'Print one sheet at home',
  landingMini3: 'Fold it in five minutes',
  landingPlay: 'Watch a card being made',
  landingPlayLength: '1½ minutes, with music',

  // My cards (pages/home.tsx), 2026-10-17
  accountButton: 'Your account: {name}',
  cardsCount: '{n} cards',
  cardsCountOne: '1 card',
  findCard: 'Find a card',
  noCardsFound: 'No card called “{q}”.',
  cardMenu: 'More for “{title}”',
  saveCardShort: 'Save to a file',
  openCardShort: 'Open a card file',
  folderShort: 'Keep in a folder',
  folderOnShort: 'Kept in “{name}”',
  folderFixShort: 'Folder needs your OK',
  folderErrorShort: 'Folder not saving',
  folderMenuTitle: 'Your card folder',
  noCardsYet: 'No cards yet. Make your first one: it takes a minute.',
  // the new-card steps, all on one page
  wizTitle: 'Make a new card',
  wizStep1: 'Name your card',
  wizStep2: 'Choose a design',
  wizStep3: 'Pick your pictures',
  wizStep4: 'Choose a theme',
  wizStep5: 'Fill the card',
  wizNameHint: 'Only you see this name: who the card is for, say.',
  wizAddNew: 'Add pictures from this device',
  wizReuse: 'Or use pictures you already have',
  wizReuseHint: 'Click a picture to use it again; it is copied into the new card.',
  wizReuseNone: 'Pictures you add to your cards show here, to use again in a new one.',
  wizSelectAll: 'All {n}',
  wizNew: 'New',
  wizChosen: '{n} pictures chosen',
  wizChosenOne: '1 picture chosen',
  wizChosenNone: 'No pictures chosen yet - you can add them later too, inside the card.',
  wizMoreLater: 'You can add more pictures later, inside the card, at any time.',
  wizRemove: 'Remove',
  wizThemeHint: 'A theme sets the paper and a first message. Both can be changed in the card.',
  wizAuto: 'Fill it for me',
  wizAutoAbout: 'Your pictures go where they look best: the best photo of people in the big circle, faces centred, the calmest picture behind the message. Change anything after.',
  wizManual: 'I will place them myself',
  wizManualAbout: 'Start with empty frames, and click each one to choose its picture.',
  wizRecommended: 'Recommended',
  wizCreate: 'Create my card',
  wizCreating: 'Making your card…',
  wizCopying: 'Adding the pictures… {n} of {of}',
  wizNeedName: 'Give the card a name first.',
  theme_generic: 'Any occasion',
  theme_birthday: 'Birthday',
  theme_autumn: 'Autumn',
  theme_baby: 'New baby',
  theme_spring: 'Spring',
  theme_graduation: 'Graduation',
  theme_christmas: 'Christmas',
  theme_love: 'Love',
  theme_thanks: 'Thank you',
  theme_congrats: 'Congratulations',
  themeMsg_generic: 'With love',
  themeMsg_birthday: 'Happy Birthday!',
  themeMsg_baby: 'Welcome, little one!',
  themeMsg_spring: 'Thinking of you',
  themeMsg_graduation: 'Congratulations, graduate!',
  themeMsg_autumn: 'Warm autumn wishes',
  themeMsg_christmas: 'Merry Christmas!',
  themeMsg_love: 'I love you',
  themeMsg_thanks: 'Thank you!',
  themeMsg_congrats: 'Congratulations!',
  pattern_leaves: 'Leaves',

  // the editor, 2026-10-17
  fillMyCard: 'Fill my card',
  fillAgain: 'Another arrangement',
  fillTitle: 'Puts your pictures in the frames, the best one in the big circle and every face centred. Press again for another arrangement.',
  fillNoPictures: 'Add a few pictures to this card first: then “Fill my card” puts them in place.',
  fillWorking: 'Arranging your pictures…',
  fillDone: 'Arranged {n} frames.',
  fillDoneOne: 'Arranged 1 frame.',
  fillAll: 'All frames are filled already: press again for another arrangement.',
  popPictures: 'Choose a picture',
  popThisCard: 'This card',
  popLibrary: 'Picture library',
  popMine: 'My other pictures',
  popMineHint: 'Pictures from your other cards. Click one to copy it into this card and put it here.',
  popMineNone: 'Your other cards have no pictures yet.',
  popAddPictures: 'Add pictures',
  popNoPictures: 'No pictures on this card yet: add some, or take one from the picture library.',
  popThisFrame: 'This frame',
  unlockToMove: 'Unlock to move',
  lockedShort: 'Locked',
  lockOn: 'Pictures are locked: unlock one to move it',
  lockOff: 'Pictures move as soon as you drag them',
  zoomFrame: 'Zoom in on this frame',
  zoomWhole: 'Whole card',
  moveShape: 'Move the shape',
  moveShapeHint: 'Drag it up or down on the card, or use the arrows.',
  moveShapeView: 'Drag the shape in Reveal 1, where the two halves meet.',
  showReveal1: 'Go to Reveal 1',
  shapeUp: 'Move the shape up',
  shapeDown: 'Move the shape down',
  messageEdit: 'Write the message',
  noteHere: 'Words on this frame',
  messageMore: 'Font, size and colour',
  panelHide: 'Hide the tools',
  panelShow: 'Show the tools',
  removeShort: 'Remove',
  backToEditing: 'Back to editing',
  backToCard: 'Back to the card',

  // the print page, 2026-10-17: pictures, not paragraphs
  printPaperShort: '{paper} paper',
  printPaperSub: 'plain, in the tray',
  printScaleShort: 'Actual size',
  printScaleSub: 'scale 100%, not “fit”',
  printDuplexShort: 'Both sides',
  printDuplexSub: 'flip on the long edge',
  printFoldShort: 'Fold it',
  printFoldSub: 'watch the steps',
  printNow: 'Print',
  printNoDuplex: 'Printer can’t do both sides?',
  printOne1: 'Print side 1',
  printOne2: 'then side 2',
  printOneFlip: 'Between them, turn the sheet over like a book page, top still at the top, and put it back in the tray.',
  nextHowToFold: 'Next: how to fold',

  // the envelope (pages/envelope.tsx, envelope/), 2026-10-17
  envMake: 'Envelope',
  printEnvelope: 'Print its envelope',
  printEnvelopeHint: 'An envelope made to fit this card when folded, on a sheet of its own',
  envTitle: 'An envelope for your card',
  envNone: 'No envelope we can print on one sheet is big enough for this card: folded, it measures {w} × {h} mm. A 9 × 12 in envelope from a shop (C4 outside the US) holds it.',
  envChoose: 'Choose an envelope',
  env_classic: 'Classic',
  env_straight: 'Straight flap',
  env_rounded: 'Rounded',
  env_heart: 'Heart',
  env_scallop: 'Scalloped',
  env_pillow: 'Pillow',
  env_pocket: 'No scissors',
  envAbout_classic: 'The pointed flap of a classic envelope.',
  envAbout_straight: 'A wide square flap, like an invitation’s.',
  envAbout_rounded: 'A soft round flap, and round sides.',
  envAbout_heart: 'The flap ends in a heart.',
  envAbout_scallop: 'A flap with a wavy edge.',
  envAbout_pillow: 'A little pillow: a tube whose curved ends push in. One strip of glue.',
  envAbout_pocket: 'The whole sheet, folded and glued - no scissors.',
  envFits: 'Inside: {w} × {h} mm, for your card of {cw} × {ch} mm, which lies on its side.',
  envScissors: 'Scissors',
  envGlue: 'Glue stick',
  envNoScissors: 'No scissors',
  envFront: 'Front',
  envBack: 'Back',
  envSide1: 'Printed side 1 (inside)',
  envSide2: 'Printed side 2 (outside)',
  envName: 'Name on the front',
  envNamePlaceholder: 'e.g. Eva - or leave it empty to write it by hand',
  envOutside: 'Outside',
  envMatchCard: 'Your card’s paper',
  envPlain: 'Plain white',
  envSteps: 'How to make it',
  envPrint: 'Print',
  envPrintHint: 'US Letter, actual size (100%), both sides flipped on the long edge - like the card. With a plain outside, only side 1 prints.',
  envStoreA6: 'Buying envelopes instead? A6 envelopes (4¾ × 6½ in) fit this card.',
  envStoreSquare: 'Buying envelopes instead? A 5 × 5 in square envelope fits this card.',
  envTrial: 'New: these envelopes have not been folded on paper by us yet. Print one, make it, and tell us how it went.',
  envFor: 'For',
  envGlueWord: 'glue',
  envCardHere: 'your card goes here',
  envSealBack: 'seal',
  envLegendCut: 'cut on the solid line',
  envLegendFold: 'fold on the dashed lines, in number order',
  envLegendGlue: 'glue the grey patches',
  envStepCut: 'Cut out the envelope along the solid line - and the round seal, if there is one.',
  envStepSides: 'Fold the two side flaps in (fold 2).',
  envStepBottom: 'Glue the grey patches on the bottom flap and fold it up onto the side flaps (fold 3).',
  envStepCardIn: 'Slide your card in, lying on its side.',
  envStepClose: 'Fold the top flap down (fold 5) and glue the grey dot - or stick the seal on over the tip.',
  envStepCrease: 'Press along every dashed line with a pen - the curved ones too - and fold them.',
  envStepTube: 'Glue the grey strip and roll the sheet into a tube, the plain strip tucked inside under the grey one.',
  envStepPillowEnds: 'Push one curved end in, slide your card in, then push the other end in.',
  envStepPocketSides: 'Glue the two grey strips and fold the sides in flat (fold 1).',
  envStepPocketBottom: 'Fold the bottom up: it becomes the front, glued to the sides (fold 2).',
  envStepPocketClose: 'Fold the top down (fold 4) and tuck it in front of the card, or glue it shut.',
};

export type StringKey = keyof typeof en;

export function t(key: StringKey, vars: Record<string, string | number> = {}): string {
  return en[key].replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/** Whether there are words for this key - for keys made up at run time, like a log action's. */
export const hasString = (key: string): key is StringKey => key in en;

/** "Chrome on Windows", from a browser's user-agent string. */
export function deviceName(ua: string | null | undefined): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'A browser';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone or iPad' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

export function ago(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}
